import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { motion } from 'framer-motion';
import logo from '../images/logo.png';
import { AuthContext } from '../auth/AuthContext';

const Navbar = () => {
  const { currentUser, userLogout } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const links = [
    { label: 'Home', path: '/' },
    { label: 'Track', path: '/issues' },
    { label: 'Reviews', path: '/reviews' },
    { label: 'Contact Us', path: '/contact/form' },
  ];

  if (currentUser) {
    links.push({ label: 'Dashboard', path: '/dashboard' });
  }

  const handleLogout = () => {
    userLogout();
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const drawer = (
    <Box
      sx={{
        width: 240,
        pt: 5,
        backgroundColor: 'rgba(0, 60, 30, 0.9)',
        height: '100%',
        color: '#fff',
      }}
    >
      <List>
        {links.map(({ label, path }) => (
          <ListItem
            button
            key={label}
            onClick={() => {
              navigate(path);
              setDrawerOpen(false);
            }}
            sx={{
              '&:hover': { backgroundColor: 'rgba(141, 240, 159, 0.2)' },
            }}
          >
            <ListItemText primary={label} />
          </ListItem>
        ))}

        {currentUser ? (
          <ListItem
            button
            onClick={() => {
              handleLogout();
              setDrawerOpen(false);
            }}
            sx={{
              '&:hover': { backgroundColor: 'rgba(141, 240, 159, 0.2)' },
            }}
          >
            <ListItemText primary="Logout" />
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              navigate('/login');
              setDrawerOpen(false);
            }}
            sx={{
              '&:hover': { backgroundColor: 'rgba(141, 240, 159, 0.2)' },
            }}
          >
            <ListItemText primary="Login" />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={3}
      sx={{
        backgroundColor: 'rgba(141, 240, 159, 0.8)',
        color: '#003c1e',
        backdropFilter: 'blur(6px)',
      }}
      component={motion.div}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Toolbar>
        {/* Logo + Brand */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ flexGrow: 1, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <img src={logo} alt="Logo" style={{ height: 45, marginRight: 12 }} />
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: '#003c1e', letterSpacing: 1 }}
          >
            MazGuard
          </Typography>
        </Box>

        {/* Links + Buttons */}
        {isMobile ? (
          <>
            <IconButton color="inherit" edge="end" onClick={toggleDrawer}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
            >
              {drawer}
            </Drawer>
          </>
        ) : (
          <Box display="flex" gap={2} alignItems="center">
            {links.map(({ label, path }) => (
              <Button
                key={label}
                onClick={() => navigate(path)}
                sx={{
                  color: '#003c1e',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    color: '#0a7d40',
                    backgroundColor: 'rgba(141, 240, 159, 0.2)',
                  },
                }}
              >
                {label}
              </Button>
            ))}

            {currentUser ? (
              <Button
                onClick={handleLogout}
                variant="contained"
                sx={{
                  borderRadius: 5,
                  backgroundColor: '#ff7043',
                  '&:hover': { backgroundColor: '#e64a19' },
                }}
              >
                Logout
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                variant="contained"
                sx={{
                  borderRadius: 5,
                  backgroundColor: '#0a7d40',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#066b35' },
                }}
              >
                Login
              </Button>
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
